import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../../components/Flex";
import Input from "../../../../../../../../components/Input";
import LoadingOverlay from "../../../../../../../../components/LoadingOverlay";
import Text from "../../../../../../../../components/Text";
import ConfirmationModal from "../../../../../../../../components/app/ConfirmationModal";
import {
  CHART_COLORS,
  CHART_DIMENSIONS,
} from "../../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../../components/useRoleController";
import { useLoading } from "../../../../../../../../hooks/use-loading";
import { useLogoutTask } from "@/hooks/use-logout-task";
import { useVisitNoteFormOptions } from "@/hooks/use-visit-note-form-options";
import { useNavigationGuard } from "../../../../../../../../hooks/use-navigation-guard";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../../../../hooks/use-reduce-disclosure";
import { useUnloadBeacon } from "../../../../../../../../hooks/use-unload-beacon";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useEncounterArchive } from "../../../../../../../../queries/encounter/use-encounter-archieve";
import { useEncounterCreate } from "../../../../../../../../queries/encounter/use-encounter-create";
import { useEncounterSummeryV2 } from "../../../../../../../../queries/encounter/use-encounter-summery-v2";
import { useEncounterUpdate } from "../../../../../../../../queries/encounter/use-encounter-update";
import { useGetAppointment } from "../../../../../../../../queries/use-get-appointment";
import { EncounterSumaryFormTypeEnum } from "../../../../../../../../services";
import { formatDateTime } from "../../../../../../../../utils/date-time-utility";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
  mergeDateTime,
  showToastOnApiCall,
} from "../../../../../../../../utils/utils";
import VisitNoteForms from "../../../../../scheduling/appointment/encounter/content-section/VisitNoteForms";
import {
  ENCOUNTER_INITIAL_VITALS,
  FORM2PAYLOAD_INITIAL_VALUE,
  Form2Payload,
  STEPS,
} from "../../../../../scheduling/appointment/encounter/encounter-constant";
import PatientChartAssessment from "../../assessment/PatientChartAssessment";
import ClinicalLayoutNew from "../../clinical-details/ClinicalLayoutNew";
import PatientCommunicationLayoutNewChart from "../../communication/PatientCommunicationLayoutNewChart";
import { useVisitNotePanel } from "../../context/VisitNotePanelContext";
import DemoGraphicsLayoutNew from "../../demographics/DemoGraphicsLayout";
import PatientEducationMaterial from "../../education-material/PatientDocLayout";
import FinancialLayout from "../../financial/FinancialLayout";
import PatientChartForms from "../../forms/PatientChartForms";
import HistoryLayoutNew from "../../history/HistoryLayoutNew";
import PatientOrderNewLayout from "../../order/PatientOrderLayout";
import OrderPrescriptionNew from "../../order/prescription/OrderPrescriptionNew";
import PatientChronologicalAndRequiringActionLayout from "../../patient-chronological-records-and-requiring-actions/patientChronologicalAndRequiringActionLayout";
import PatientReferral from "../../referral/PatientReferral";
import PatientTask from "../../tasks/PatientTask";
import PatientVisitHistory from "../../visit-history/PatientVisitHistory";
import VisitNoteConfirmationModal from "../VisitNoteConfirmationModal";
import AiScriberModal from "../ai-scriber/AiScriberModal";
import AiScribeV2Window from "../ai-scriber/v2/AiScribeV2Window";
import { IS_AI_SCRIBE_V2_ENABLED } from "../ai-scriber/v2/constants";
import {
  clearScribeSnapshot,
  hasScribeSnapshot,
} from "../ai-scriber/v2/scribe-session-storage";
import VisitNoteTemplatePreview from "../template-preview/VisitNoteTemplatePreview";
import ActiveForm from "./components/ActiveForm";
import {
  IS_SCRIBE_HIDDEN_ON_PREMIER_NOTES,
  NOTE_HEADER_ACTIONS_ID,
  NOTE_REQUIRED_SUMMARY_ID,
} from "./contant/encounter-constant";
import EncounterTabs, { EncounterTab } from "./components/EncounterTabs";
import FormsAndAssessmentTab from "./components/FormsAndAssessmentTab";
import StartScribeButton from "./components/StartScribeButton";
import FormActions from "./components/layout-components/FormActions";
import { PendingSectionSavesProvider } from "./note-forms/readmission-f2f";
import { INITIAL_F2F_FORM_TYPES } from "./note-forms/initial-f2f/initial-f2f-form-options-constant";

const ActiveContentComponent = {
  "clinical-details": <ClinicalLayoutNew />,
  index: <PatientChronologicalAndRequiringActionLayout />,
  "visit-history": <PatientVisitHistory />,
  order: <PatientOrderNewLayout />,
  prescription: <OrderPrescriptionNew />,
  documents: <PatientEducationMaterial />,
  history: <HistoryLayoutNew />,
  forms: <PatientChartForms />,
  assessment: <PatientChartAssessment />,
  demographics: <DemoGraphicsLayoutNew />,
  financial: <FinancialLayout />,
  communication: <PatientCommunicationLayoutNewChart />,
  referral: <PatientReferral />,
  tasks: <PatientTask />,
};

// Prescriptions, immunizations, orders and therapies are rendered by the plan
// section from their own appointment-keyed list APIs, none of which re-run on a
// form reset. Their query keys are invalidated so the section repopulates.
const PLAN_SECTION_QUERY_KEYS = [
  "patient-prescription-list",
  "patient-vaccine-list",
  "patient-orders-list",
  "all-therapy-notes",
];

type NoteFormType = EncounterSumaryFormTypeEnum;

const PatientEncounter = () => {
  const { patientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useLoading();
  const appointmentId = useQueryParams("appointmentid");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<EncounterTab>("visit-note");
  const [activeStep, setActiveStep] = useState<string>(STEPS[1]?.value);
  const { resolveFormType } = useVisitNoteFormOptions();
  const [currentForm, setCurrentForm] = useState<NoteFormType>(() =>
    resolveFormType(searchParams.get("noteType"))
  );
  const { mutateAsync: archiveEncounterForm } = useEncounterArchive();
  const [isPanelExpanded, setIsPanelExpandedLocal] = useState(true);
  const { setIsPanelExpanded: setIsPanelExpandedContext } = useVisitNotePanel();
  const setIsPanelExpanded = useCallback(
    (value: boolean) => {
      setIsPanelExpandedLocal(value);
      setIsPanelExpandedContext(value);
    },
    [setIsPanelExpandedContext]
  );
  const { mutateAsync: createEncounter } = useEncounterCreate();
  const { mutateAsync: updateEncounter } = useEncounterUpdate();
  const showSnackbar = useSnackbar();
  const [
    aiScriberDisclosureHost,
    resetDisclosureHostForm,
    VisitNoteTemplatePreviewDisc,
    navigationBlockerDisclosure,
    resetModalityDisclosure,
  ] = useReduceDisclosure(5);
  const activePin = useQueryParams("activePin") || "index";

  // A scribe interrupted by refresh/offline persists its transcript for this
  // visit; reopen the window once so the provider lands back on the recovered
  // (paused) session instead of an empty Start Scribe button.
  const didRecoverScribeRef = useRef(false);
  useEffect(() => {
    if (didRecoverScribeRef.current || !appointmentId) return;
    didRecoverScribeRef.current = true;
    if (IS_AI_SCRIBE_V2_ENABLED && hasScribeSnapshot(appointmentId)) {
      aiScriberDisclosureHost.open();
    }
  }, [appointmentId, aiScriberDisclosureHost]);

  // Leaving the encounter (or signing it off) must drop any recoverable scribe.
  // This is done in the explicit leave handlers below, not on unmount — the
  // offline screen and a page refresh both unmount this route, and those are the
  // cases recovery is meant to survive.
  const scribeKeyRef = useRef(appointmentId);
  scribeKeyRef.current = appointmentId;

  // react-router drops every registered blocker the moment any navigation
  // completes ("on a successful navigation we can assume we got through all
  // blockers"). This page rewrites its own search params from the prefill
  // effect, an 8 second batch timer and a mutation subscription, and each of
  // those rewrites is a navigation. One landing between "End This Encounter?"
  // opening and the save finishing throws the blocked back-navigation away, so
  // releasing it afterwards releases nothing - the provider watches the save
  // spinner and stays on the encounter. Param rewrites are therefore held from
  // the moment navigation is blocked until the encounter has actually been
  // left.
  const isLeavingRef = useRef(false);
  const isNavigationBlockedRef = useRef(false);
  const shouldHoldSearchParams = useCallback(
    () => isNavigationBlockedRef.current || isLeavingRef.current,
    []
  );

  const { data: appointmentData } = useGetAppointment({
    appointmentUuid: appointmentId ?? undefined,
    providerGroupId: getXtenantId() ?? "",
  });

  useEffect(() => {
    setIsPanelExpandedContext(true);
    return () => {
      setIsPanelExpandedContext(false);
    };
  }, [setIsPanelExpandedContext]);

  useEffect(() => {
    if (appointmentData?.data) {
      formik.setValues({
        ...formik.values,
        // to prefill appointment provider
        chiefComplaint: appointmentData?.data?.chiefComplaint,
        providerName: appointmentData?.data?.providerName as any,
        providerId: appointmentData?.data?.providerId as any,
      });
    }
  }, [appointmentData]);

  const {
    data: encounterSnapshotData,
    refetch: refetchPrefillSummeryData,
    isLoading: isPrefillLoading,
  } = useEncounterSummeryV2({
    appointmentUuid: appointmentId!,
    xTENANTID: getXtenantId(),
  });

  const prefillEncounterSummery = encounterSnapshotData?.data as any;
  const transFormedIcdCodes = useMemo(
    () =>
      prefillEncounterSummery?.assessmentCodes?.map((it: any) => ({
        value: it?.uuid,
        code: `${it?.code} - ${it?.description || ""}`,
      })),
    [prefillEncounterSummery?.assessmentCodes]
  );

  const transFormedCptCodes = useMemo(() => {
    return prefillEncounterSummery?.procedureCodes?.map((it: any) => ({
      value: it?.uuid,
      code: `${it?.code} - ${it?.description || ""}`,
    }));
  }, [prefillEncounterSummery?.procedureCodes]);

  useEffect(() => {
    if (prefillEncounterSummery) {
      setIsLoading(true);
      const patientVitals = prefillEncounterSummery?.patientVitals
        ? ENCOUNTER_INITIAL_VITALS?.map((it) => {
            const vitalToPrefill = prefillEncounterSummery?.patientVitals?.find(
              (v: any) => v?.name === it?.name
            );

            return it?.name === vitalToPrefill?.name
              ? { ...vitalToPrefill, selected: true }
              : it;
          })
        : ENCOUNTER_INITIAL_VITALS;

      const transformedData = {
        ...prefillEncounterSummery,
        patientVitals,
        assessmentCodes: transFormedIcdCodes,
        procedureCodes: transFormedCptCodes,
      };

      formik.setValues(transformedData);

      if (
        prefillEncounterSummery?.encounterStatus &&
        prefillEncounterSummery?.encounterStatus !== "COMPLETED"
      ) {
        const lastIndex = STEPS.findIndex(
          (v) => v.value === prefillEncounterSummery.encounterStatus
        );

        setActiveStep((prev) =>
          lastIndex !== STEPS.length ? STEPS[lastIndex + 1]?.value : prev
        );
      }

      if (
        (currentForm && currentForm === prefillEncounterSummery?.formType) ||
        prefillEncounterSummery?.formType
      ) {
        setCurrentForm(prefillEncounterSummery?.formType);
        // Merge so an active panel selection (activePin / clinicalSection set
        // from the Quick Health Records "View All") survives prefill refetches.
        if (!shouldHoldSearchParams())
          setSearchParams(
            (prev) => {
              prev.set("appointmentid", appointmentId ?? "");
              prev.set("noteType", prefillEncounterSummery?.formType);
              return prev;
            },
            { replace: true }
          );
      }

      setIsLoading(false);
    }
  }, [prefillEncounterSummery]);

  // Snapshot returned an existing encounter: flip the gate so the individual
  // prefill APIs (medical history, vitals, allergies, etc.) start fetching.
  useEffect(() => {
    if (
      prefillEncounterSummery &&
      searchParams.get("hasEncounterCreation") === "true" &&
      !shouldHoldSearchParams()
    ) {
      const next = new URLSearchParams(searchParams);
      next.set("hasEncounterCreation", "false");
      setSearchParams(next, { replace: true });
    }

    // batch calls for apis, after `8 sec`
    const batchTimer = setTimeout(() => {
      if (shouldHoldSearchParams()) return;
      const next = new URLSearchParams(searchParams);
      next.set("hasEncounterCreation", "false");
      setSearchParams(next, { replace: true });
    }, 8000);

    // Drop the pending param rewrite when the encounter is left, so it cannot
    // fire against a history entry the user has already navigated away from.
    return () => clearTimeout(batchTimer);
  }, [prefillEncounterSummery]);

  const queryClient = useQueryClient();

  // First successful create/update mutation also dismisses the gate so the
  // newly-added record is fetched by the corresponding list GET.
  useEffect(() => {
    if (searchParams.get("hasEncounterCreation") !== "true") return;
    const unsubscribe = queryClient.getMutationCache().subscribe((event) => {
      if (
        // appointment status we are updating, because of that
        event?.mutation?.state?.data?.data?.code !== "APPOINTMENT_UPDATED" &&
        event?.type === "updated" &&
        event.mutation?.state?.status === "success" &&
        // The save that runs on the way out settles here too - rewriting the
        // params now would clear the blocker holding the exit navigation.
        !shouldHoldSearchParams()
      ) {
        const next = new URLSearchParams(searchParams);
        next.set("hasEncounterCreation", "false");
        setSearchParams(next, { replace: true });
      }
    });
    return unsubscribe;
  }, [queryClient, searchParams, setSearchParams]);

  const shouldDisableToast = useRef<boolean>(false);

  const saveEncounter = (v: Form2Payload) => {
    //Todo: need to remove
    // if (v?.formType === "TREATMENT_PLAN" && !v?.agreedToTreatmentPlan) {
    //   showSnackbar({
    //     message: "Please agree to the treatment plan",
    //     type: "error",
    //   });

    //   throw new Error();
    // }

    const shouldUpdateCurForm =
      prefillEncounterSummery &&
      prefillEncounterSummery?.formType === currentForm;

    const patientVitals = v?.patientVitals?.map((it) => {
      return it?.value || it?.systolic || it?.diastolic
        ? { ...it, selected: true }
        : it;
    });

    const scheduleTime =
      v?.nextAppointmentDate && v?.nextAppointmentTime
        ? mergeDateTime(
            new Date(v?.nextAppointmentDate),
            new Date(v?.nextAppointmentTime) as any
          )
        : undefined;

    const payload: any = {
      encounterSumary: {
        ...v,
        patientVitals,
        encounterStatus: activeStep,
        appointmentId: appointmentId,
        patientId: patientId,
        formType: currentForm,
        scheduleTime: scheduleTime,
      },
      xTENANTID: getXtenantId(),
    };

    // api calls to create or update encounter
    // Returned so formik's `submitForm()` resolves only once the save has
    // actually settled - callers navigate away on that promise.
    return showToastAndRefetch(
      shouldUpdateCurForm ? updateEncounter(payload) : createEncounter(payload)
    );
  };

  const showToastAndRefetch = (apiCall: Promise<any>) => {
    return apiCall
      .then((res) => {
        if (!shouldDisableToast.current) {
          showSnackbar(getSuccessSnackbar(res));
          shouldDisableToast.current = false;
        }
        refetchPrefillSummeryData();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const formik = useFormik<Form2Payload>({
    initialValues: FORM2PAYLOAD_INITIAL_VALUE,
    onSubmit: saveEncounter,
  });

  // Applies a note-template switch everywhere it is reflected: the dropdown, the
  // shareable URL and the payload. Only ever called once the switch is allowed —
  // either there is nothing to discard, or the provider confirmed.
  const applyVisitNoteFormType = (formType: NoteFormType) => {
    setCurrentForm(formType);
    // Merge so the active panel selection (activePin / clinicalSection) is
    // preserved when switching the visit note form type. Replaced rather than
    // pushed: a pushed entry sits on the same pathname, so the guard lets it
    // through silently and the provider's first back press only undoes the
    // note-type switch instead of leaving the encounter.
    setSearchParams(
      (prev) => {
        prev.set("appointmentid", appointmentId ?? "");
        prev.set("noteType", formType);
        return prev;
      },
      { replace: true }
    );
    formik.setFieldValue("formType", formType);
  };

  // Archives the saved encounter summary, refetches, and resets the form.
  // Shared by the note-type switch and the primary-modality switch; the caller
  // supplies what should happen once the form has been cleared.
  const archiveAndResetForm = (onArchived: () => void) => {
    // The header fields are prefilled from the appointment exactly once, so a
    // plain resetForm() drops them for good and they stop reaching the save
    // payload. Carry the on-screen value across the reset, falling back to the
    // appointment when the form never received one.
    const appointment = appointmentData?.data;
    const headerValues = {
      chiefComplaint:
        formik.values?.chiefComplaint ?? appointment?.chiefComplaint,
      providerName: formik.values?.providerName ?? appointment?.providerName,
      providerId: formik.values?.providerId ?? appointment?.providerId,
    };

    showToastOnApiCall({
      apiCall: archiveEncounterForm({
        encounterSummaryId: (prefillEncounterSummery as any)?.uuid,
        status: true,
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        refetchPrefillSummeryData();
        formik.resetForm({
          values: { ...FORM2PAYLOAD_INITIAL_VALUE, ...headerValues },
        });
        onArchived();
      },
    });
  };

  const handleConfirmResetForm = () => {
    const nextForm = resetDisclosureHostForm?.data?.formType as
      NoteFormType | undefined;

    if (!nextForm) {
      resetDisclosureHostForm.close();
      return;
    }

    archiveAndResetForm(() => {
      resetDisclosureHostForm.close();
      applyVisitNoteFormType(nextForm);
    });
  };

  // Switching the primary modality (CBT / DBT / ACT / …) discards the note the
  // same way switching the note type does: archive the saved encounter summary,
  // then start a clean form on the newly picked modality.
  const handleModalityChange = (modality: string) => {
    const savedModality = (prefillEncounterSummery as any)?.primaryModality;

    if (
      (prefillEncounterSummery as any)?.uuid &&
      savedModality &&
      savedModality !== modality
    ) {
      resetModalityDisclosure.open({ modality });
      return;
    }

    formik.setFieldValue("primaryModality", modality);
  };

  const handleConfirmModalityChange = () => {
    const nextModality = resetModalityDisclosure?.data?.modality;

    archiveAndResetForm(() => {
      resetModalityDisclosure.close();
      // Keep the user on the same note type, on the modality they just picked.
      formik.setFieldValue("formType", currentForm);
      formik.setFieldValue("primaryModality", nextModality);
      PLAN_SECTION_QUERY_KEYS.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    });
  };

  const renderActiveContentComponent = () => (
    <ActiveForm
      formik={formik}
      curForm={currentForm}
      onModalityChange={handleModalityChange}
    />
  );

  const handleConfirmReset = () => {
    clearScribeSnapshot(scribeKeyRef.current ?? undefined);
    shouldDisableToast.current = true;
    isLeavingRef.current = true;
    formik
      .submitForm()
      .catch(() => undefined)
      .finally(() => {
        navigationBlockerDisclosure.close();

        // Use navigateWithoutGuard for intentional navigation after save
        navigateWithoutGuard(() => {
          navigate(`/app/provider/patients/new-patient/${patientId}/index`);
        });
      });
  };

  const { hasNone: hasNoneAIScriber } = useRoleController("Generate Note");
  const isScribeHiddenForForm =
    IS_SCRIBE_HIDDEN_ON_PREMIER_NOTES &&
    INITIAL_F2F_FORM_TYPES.includes(currentForm);

  // `useDisclosure` hands back a fresh `open`/`close` pair on every render, so
  // the guard callback is kept stable through a ref. An inline arrow re-runs
  // the guard effect on each render while navigation is blocked, which re-opens
  // this confirmation the moment it is dismissed.
  const navigationBlockerRef = useRef(navigationBlockerDisclosure);
  navigationBlockerRef.current = navigationBlockerDisclosure;

  const handleNavigationAttempt = useCallback(
    () => navigationBlockerRef.current.open(),
    []
  );

  // Navigation guard - show confirmation when leaving this form
  const {
    proceed: proceedNavigation,
    reset: resetNavigation,
    navigateWithoutGuard,
    isBlocked,
  } = useNavigationGuard({
    onNavigationAttempt: handleNavigationAttempt,
  });

  // The save below is awaited before leaving, by which point `proceedNavigation`
  // has been rebuilt around a newer blocker. Calling it through a ref releases
  // the blocker that is actually live instead of the one captured on click.
  const proceedNavigationRef = useRef(proceedNavigation);
  proceedNavigationRef.current = proceedNavigation;
  isNavigationBlockedRef.current = isBlocked;

  // `proceed()` only releases a blocker that is still in the `blocked` state.
  // Should react-router have cleared it anyway, walking the history entry back
  // by hand still gets the provider off the encounter instead of leaving them
  // on a form that no longer responds to the back button.
  const leaveEncounter = useCallback(() => {
    clearScribeSnapshot(scribeKeyRef.current ?? undefined);
    if (isNavigationBlockedRef.current) {
      proceedNavigationRef.current();
      return;
    }

    navigateWithoutGuard(() => navigate(-1));
  }, [navigate, navigateWithoutGuard]);

  // Auto-save on page unload/reload using beacon
  useUnloadBeacon("/api/encounter/auto-save", () => {
    // Trigger formik submission and return the form data
    if (!(formik.dirty && formik.values)) return null;

    // Submit the form silently
    shouldDisableToast.current = true;
    formik.handleSubmit();
  });

  // Idle timeout (and any other sign-out) persists the note silently instead of
  // trapping the provider behind the "End This Encounter?" prompt. Awaited by
  // logout so the save lands while the auth token is still present.
  useLogoutTask(async () => {
    if (!(formik.dirty && formik.values)) return;
    shouldDisableToast.current = true;
    await formik.submitForm();
  });

  const handleConfirmNavigation = () => {
    navigationBlockerDisclosure.close();
    shouldDisableToast.current = true;
    // Freeze this page's param rewrites first: the save settling is what wakes
    // the mutation subscription and the prefill refetch, and either of them
    // navigating would clear the blocker before it can be released.
    isLeavingRef.current = true;
    // The blocked navigation is released however the save settles. A rejected
    // submit used to leave the blocker stuck in "blocked", stranding the user
    // on the encounter with no way back.
    formik.submitForm().finally(leaveEncounter);
  };

  const handleCancelNavigation = () => {
    navigationBlockerDisclosure.close();
    // Staying puts the page back in service, so its param rewrites resume. The
    // blocker is released first, then the gate flip that was held while the
    // confirmation was up is applied - the 8 second timer that would otherwise
    // have carried it has already run, and leaving the gate on keeps every
    // individual prefill API switched off.
    isLeavingRef.current = false;
    resetNavigation();

    if (searchParams.get("hasEncounterCreation") === "true")
      setSearchParams(
        (prev) => {
          prev.set("hasEncounterCreation", "false");
          return prev;
        },
        { replace: true }
      );
  };

  const handleVisitNoteFormOptionClick = (v: NoteFormType) => {
    // Nothing is applied while the confirmation is open: the switch used to run
    // first and left "No" with nothing to undo, so the dropdown, the URL and the
    // form had already moved to the new template.
    if (prefillEncounterSummery && prefillEncounterSummery?.formType !== v) {
      resetDisclosureHostForm.open({ formType: v });
      return;
    }

    applyVisitNoteFormType(v);
  };

  return (
    <PendingSectionSavesProvider>
      <Flex width="100%" height="100%" position="relative">
        <ConfirmationModal
          maxWidth={"sm"}
          onConfirm={handleConfirmResetForm}
          disclosureHost={resetDisclosureHostForm}
          confirmationText="Yes"
          cancelText="No"
          title={
            <Text fontWeight={"medium"} fontSize={"24px"}>
              Are you sure you want to reset the previous Note Template?
            </Text>
          }
        />

        <ConfirmationModal
          maxWidth={"sm"}
          onConfirm={handleConfirmModalityChange}
          disclosureHost={resetModalityDisclosure}
          confirmationText="Yes"
          cancelText="No"
          title={
            <Text fontWeight={"medium"} fontSize={"24px"}>
              Are you sure you want to reset the session type?
            </Text>
          }
        />

        {/* Navigation Blocker Confirmation Modal */}
        <VisitNoteConfirmationModal
          onConfirm={
            navigationBlockerDisclosure?.data?.useGaurd
              ? handleConfirmReset
              : handleConfirmNavigation
          }
          disclosureHost={{
            ...navigationBlockerDisclosure,
            close: handleCancelNavigation, // Override close to handle navigation reset
          }}
        />

        {/* Left Panel - Timeline */}
        <Flex
          flexDirection="column"
          width="50%"
          height="100%"
          gap={1.5}
          sx={{
            zIndex: isPanelExpanded ? 1 : 2,
            filter: isPanelExpanded ? "blur(0.3px)" : "none",
            transition: "filter 0.3s ease-in-out",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* <Outlet /> */}
          {
            ActiveContentComponent[
              activePin as keyof typeof ActiveContentComponent
            ]
          }
        </Flex>

        {/* Right Panel - Visit Note Form */}
        <Flex
          flexDirection="column"
          bgcolor={"green"}
          flex={1}
          height="100%"
          sx={{
            width: isPanelExpanded ? "94%" : "50%",
            position: isPanelExpanded ? "absolute" : "relative",
            top: isPanelExpanded ? 0 : "auto",
            right: isPanelExpanded ? 0 : "auto",
            zIndex: isPanelExpanded ? 4 : 1,
            transition: "all 0.3s ease-in-out",
            boxShadow: isPanelExpanded
              ? "0px 4px 20px rgba(0, 0, 0, 0.15)"
              : "none",
            borderRadius: "8px",
            border: isPanelExpanded
              ? CHART_DIMENSIONS.encounterPanelExpandBorder
              : `1px solid ${CHART_COLORS.borderLight}`,
          }}
        >
          {/* Header Row: Tabs (left) + Expand/Close (right) */}
          <EncounterTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            isPanelExpanded={isPanelExpanded}
            onToggleExpand={() => setIsPanelExpanded(!isPanelExpanded)}
            onClose={() => navigationBlockerDisclosure.open({ useGaurd: true })}
          />

          {/* Form Selection Row (only for Visit Note tab) */}
          {activeTab === "visit-note" && (
            <Flex
              gap={1.5}
              alignItems="center"
              p={1.5}
              sx={{
                borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
                backgroundColor: CHART_COLORS.surfaceWhite,
              }}
            >
              <VisitNoteForms
                selectedForm={currentForm}
                onOptionClick={handleVisitNoteFormOptionClick}
              />
              {/* <StyledButtonV2
              variant="outlined"
              size="small"
              label="Visit Note Template"
              sx={{
                minWidth: "180px",
                height: CHART_DIMENSIONS.controlHeight,
              }}
              onClick={() => VisitNoteTemplatePreviewDisc.open()}
            /> */}
              <VisitNoteTemplatePreview
                disclosureHost={VisitNoteTemplatePreviewDisc}
                onExport={(selectedNotes) => {
                  const payload = {
                    ...formik.values,
                    ...selectedNotes,
                  };
                  formik.setValues(payload);
                }}
              />

              {!hasNoneAIScriber && !isScribeHiddenForForm && (
                <StartScribeButton
                  onStart={() => aiScriberDisclosureHost.open()}
                />
              )}

              {!IS_AI_SCRIBE_V2_ENABLED && (
                <AiScriberModal disclosureHost={aiScriberDisclosureHost} />
              )}
            </Flex>
          )}

          {/**
           * V2 scribe overlays this panel only — it is absolutely positioned, so it
           * must stay a direct child of the visit-note panel and never sit inside
           * the toolbar row, otherwise it would cover the chart and sidebar.
           */}
          {IS_AI_SCRIBE_V2_ENABLED && (
            <AiScribeV2Window
              disclosureHost={aiScriberDisclosureHost}
              noteType={currentForm}
            />
          )}

          {activeTab === "visit-note" ? (
            <>
              {/* Visit Information */}
              <Flex
                p={1.5}
                sx={{
                  borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
                  backgroundColor: CHART_COLORS.surfaceWhite,
                }}
              >
                <Flex flexDirection="column" gap={1} width="100%">
                  {/* First row: Visit Time and Appointment Type */}
                  <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                    width="100%"
                  >
                    <Flex alignItems="center" gap={0.25}>
                      <Text
                        // fontSize={FILED_LABEL_FONTSIZE}
                        isTabletext
                        color={CHART_COLORS.textMutedGrey}
                      >
                        Visit Date & Time &nbsp; :
                      </Text>
                      <Text isTabletext>
                        {formatDateTime(appointmentData?.data?.startTime)}
                      </Text>
                    </Flex>
                    <Flex alignItems="center" gap={0.25}>
                      <Text
                        // fontSize={FILED_LABEL_FONTSIZE}
                        isTabletext
                        color={CHART_COLORS.textMutedGrey}
                      >
                        Appointment Type &nbsp; :
                      </Text>
                      <Text isTabletext>
                        {appointmentData?.data?.type ?? "-"}
                      </Text>
                    </Flex>
                    <Box
                      id={NOTE_HEADER_ACTIONS_ID}
                      ml="auto"
                      display="flex"
                      alignItems="center"
                    />
                  </Flex>
                  <Box id={NOTE_REQUIRED_SUMMARY_ID} display="contents" />

                  {/* Premier MD notes edit the reason for visit under Subjective */}
                  {!INITIAL_F2F_FORM_TYPES.includes(currentForm) && (
                    <>
                      <Flex alignItems="center" gap={1} mt={1}>
                        <Text
                          // fontSize={FILED_LABEL_FONTSIZE}
                          isTabletext
                          color={CHART_COLORS.textMutedGrey}
                        >
                          Reason for Visit:
                        </Text>
                        <Box flex={1}>
                          <Input
                            textFieldProps={{
                              name: "chiefComplaint",
                              value: formik.values.chiefComplaint,
                              onChange: formik.handleChange,
                              onBlur: formik.handleBlur,
                              placeholder: "Enter Reason for Visit",
                            }}
                          />
                        </Box>
                      </Flex>
                    </>
                  )}
                </Flex>
              </Flex>

              {/* SOAP Note Form */}
              <Flex
                flexDirection="column"
                p={0.5}
                flex={1}
                bgcolor={"white"}
                sx={{ overflowY: "auto" }}
              >
                <LoadingOverlay isLoading={isLoading || isPrefillLoading} />
                {renderActiveContentComponent()}
              </Flex>

              {/* Footer Actions */}
              <FormActions
                formType={currentForm}
                formik={formik}
                prefillEncounterSummery={prefillEncounterSummery}
                navigateWithoutGuard={navigateWithoutGuard}
              />
            </>
          ) : (
            <FormsAndAssessmentTab />
          )}
        </Flex>
      </Flex>
    </PendingSectionSavesProvider>
  );
};

export default PatientEncounter;
